import React, { useState, useEffect } from "react";
import { fetchData } from "../../api/utils"; // Assuming this fetchData function handles API calls.
import { capitalizeWords } from "../../utils/helpers";
import { useUser } from "../../api/AuthContext";
import { submitUserResponses, submitAssessmentOutcome, fetchAssessmentCards, fetchQuestionsAndOptions, fetchParentAssessmentDetails } from "../../api/AssessmentHelper";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import getDietRecallScore from "../../utils/getDietRecallScore";
import Modal from "../Shared/Modal";
import { Buffer } from "buffer"; 
import MedicalAssessment from "./Assessments/MedicalAssessment";
import StartAssessment from "./StartAssessment";
import AssessmentCard from './AssessmentCard';
import ViewResponses from './ViewResponses';
import { ClipLoader } from "react-spinners"; // Add loading spinner
import { generateFullAssessmentReport } from '../../utils/AssessmentPdfGenerator';
import { getUserId, getAccesstoken } from "../../utils/auth";


const User_Assessments = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [activeCardTitle, setActiveCardTitle] = useState(null);
  const [assessmentAdded, setAssessmentAdded] = useState(false);
  const [assessmentCards, setAssessmentCards] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState({});
  const [responses, setResponses] = useState({}); // Keep track of responses
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [parentAssessmentName, setParentAssessmentName] = useState(null);
  const [parentAssessmentId, setParentAssessmentId] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const [parentAssessmentDesc, setParentAssessmentDesc] = useState(null);
  const [viewingResponses, setViewingResponses] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const navigate = useNavigate();

  const userId = getUserId();
  const access_token = getAccesstoken();
  

  // create encoding to pass result component
  const encodeData = (name) => {
          const obj = { name };
          return encodeURIComponent(Buffer.from(JSON.stringify(obj)).toString("base64"));
        };

  const params = useParams();
  const location = useLocation();

  // Extract query parameters
  const searchParams = new URLSearchParams(location.search);
  

const encodedData = searchParams.get("details");

const decodeData = (data) => {
  if (!data) return { name: "Unknown", desc: "Description not available" };
  try {
    const decoded = JSON.parse(Buffer.from(decodeURIComponent(data), "base64").toString("utf-8"));
    return decoded;
  } catch (error) {
    console.error("Error decoding data:", error);
    return { name: "Unknown", desc: "Description not available" };
  }
};

const { name: assessmentName, desc: assessmentDesc } = decodeData(encodedData);

  useEffect(() => {
    const initializeAssessment = async () => {
      if (!params.assessmentId || !access_token || !userId) {
        setError('Please login to continue');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        setParentAssessmentId(params.assessmentId);
        setParentAssessmentName(assessmentName);
        setParentAssessmentDesc(assessmentDesc);

        const { cards, outcomesMap } = await fetchAssessmentCards(access_token, userId, params.assessmentId);
        // commenting this to avoid {} list of outcomeMap
        // if (cards && cards.length > 0) {
        //   setAssessmentCards(cards);
        //   setOutcomes(outcomesMap);
        // } else {
        //   setError('No assessments available for this category');
        // }
        setAssessmentCards(cards);
        setOutcomes(outcomesMap);
      } catch (error) {
        console.error('Error fetching assessment cards:', error);
        setError(error.message || 'Failed to load assessment cards');
      } finally {
        setLoading(false);
      }
    };

    initializeAssessment();
  }, [params.assessmentId, access_token, userId, assessmentName, assessmentDesc]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!activeCard || outcomes[activeCard] || !access_token) return;

      setLoading(true);
      setError(null);

      try {
        const { questions: fetchedQuestions, options: fetchedOptions } = await fetchQuestionsAndOptions(activeCard, access_token);
        setQuestions(fetchedQuestions);
        setOptions(fetchedOptions);
      } catch (error) {
        console.error('Error fetching questions:', error);
        setError("Failed to load questions.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [activeCard, access_token]);


  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  const currentQuestions = questions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStartClick = (cardId, cardTitle) => {
    if (outcomes[cardId]) {
      const encodedData = encodeData(cardTitle);
      navigate(`/user/assessment/start/${cardId}?details=${encodedData}&mode=edit`);
      window.location.reload();
    } else {
      // Change this to navigate to the new route
      const encodedData = encodeData(cardTitle);
      navigate(`/user/assessment/start/${cardId}?details=${encodedData}`);
      window.location.reload();
    }
  };

  const handleViewResponses = (assessmentId, assessmentTitle) => {
    const encodedData = encodeData(assessmentTitle);
    navigate(`/user/assessment/responses/${assessmentId}?details=${encodedData}`);
  };

  const handleResponseChange = (questionId, response) => {
    setResponses(prevResponses => ({
      ...prevResponses,
      [questionId]: response,
    }));
  };

  const handleSubmit = async () => {
    if (!questions || questions.length === 0) {
      console.error("Questions array is undefined or empty.");
      alert("Questions data is missing. Please try again.");
      return;
    }

    const createdAt = new Date().toISOString();
    const textInputAssessments = ["24 hours Diet Recall"];

    const userResponses = Object.keys(responses).map(questionId => {
      const question = questions.find(q => q.id === questionId);

      if (!question) {
        console.error(`Question not found for ID: ${questionId}`);
        return null;
      }

      const questionType = question.question_type;
      let score = 0;

      if (questionType === "text_input" && textInputAssessments.includes(activeCardTitle)) {
        const userInput = responses[questionId]?.trim();
        const numericValue = parseFloat(userInput);

        if (!isNaN(numericValue)) {
          score = getDietRecallScore(question.question_text.toLowerCase(), numericValue);
        } else {
          console.warn(`Invalid numeric input for question: ${question.question_text}`);
        }
      } else {
        const selectedResponse = Array.isArray(responses[questionId])
          ? responses[questionId].join(", ")
          : responses[questionId];

        score = calculateScore(questionId, selectedResponse, questionType);
      }

      return {
        user_id: userId,
        question_id: questionId,
        response: responses[questionId],
        score: score,  // ✅ Now the correct score should be stored
        created_at: createdAt,
      };
    }).filter(Boolean); // Remove any null values

    try {
      if (userResponses.length === 0) {
        alert("No valid responses to submit.");
        return;
      }

      await submitUserResponses(userResponses, access_token);

      const totalScore = userResponses.reduce((acc, curr) => acc + curr.score, 0);


      const assessmentOutcome = {
        user_id: userId,
        assessment_id: activeCard,
        total_score: totalScore,
        status: "completed",
        created_at: createdAt,
      };

      await submitAssessmentOutcome(assessmentOutcome, access_token);

      alert("Responses and assessment outcome submitted successfully!");

      // setassessmentAdded(true)

      setActiveCard(null);
      navigate(`/user/results/${activeCard}`, {
        state: { assessmentName: assessmentCards.find(card => card.id === activeCard)?.title },
      });
    } catch (error) {
      alert("Failed to submit responses or assessment outcome.");
    }
  };




  // const calculateScore = (questionId, selectedOptionLabel) => {
  //   const optionsForQuestion = options[questionId];
  //   const selectedOption = optionsForQuestion.find(option => option.option_label === selectedOptionLabel);
  //   return selectedOption ? selectedOption.score : 0;
  // };

  const calculateScore = (questionId, selectedResponse, questionType) => {
    if (questionType === "radio" || questionType === "single_choice") {
      const optionsForQuestion = options[questionId];
      const selectedOption = optionsForQuestion.find(option => option.option_label === selectedResponse);
      return selectedOption ? selectedOption.score : 0;
    }
    return 0;
  };

  const groupedQuestions = currentQuestions.reduce((groups, question) => {
    const section = question.section || ''; // Default to 'Uncategorized' if no section is specified
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(question);
    return groups;
  }, {});

  // const handleViewResponses = (cardId, cardTitle) => {
  //   setSelectedAssessment({
  //     id: cardId,
  //     title: cardTitle
  //   });
  //   setViewingResponses(true);
  // };

  const handleDownloadPDF = async () => {
    try {
      const parentAssessment = {
        name: parentAssessmentName || "Assessment",
        desc: parentAssessmentDesc || "No description available"
      };

      const subAssessments = assessmentCards.map(card => ({
        title: card.title,
        completed: outcomes[card.id] !== undefined,
        total_score: outcomes[card.id]?.total_score || 0,
        responses: outcomes[card.id]?.responses || []
      }));

      const pdfDoc = await generateFullAssessmentReport(parentAssessment, subAssessments, outcomes);
      pdfDoc.download(`${parentAssessment.name}_full_report.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <ClipLoader color="#36d7b7" loading={loading} size={50} />
        <p>Loading assessments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button 
          className="btn-assessment"
          onClick={() => navigate('/login')}
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (viewingResponses && selectedAssessment) {
    return (
      <ViewResponses
        assessmentId={selectedAssessment.id}
        assessmentTitle={selectedAssessment.title}
        onBack={() => {
          setViewingResponses(false);
          setSelectedAssessment(null);
        }}
      />
    );
  }

  return (
    <div>
      <div className="dashboard_grey_section">
      {/* <button
        onClick={handleDownloadPDF}
        className="btn-assessment font-xsm btns btn-animation-blue"
      >
        Download Full Assessment Report
      </button> */}
      <div className="d-flex gap-2 justify-content-end mt-2 mb-2">
    <button
        onClick={handleDownloadPDF}
        style={{
            all: "unset",
            cursor: generatingPDF ? "not-allowed" : "pointer",
            fontSize: "22px",
            marginLeft: '10px',
            color: 'var(--theme-color)',
            opacity: generatingPDF ? 0.6 : 1,
        }}
        disabled={generatingPDF}
    >
        <i className="fa-solid fa-download icon"></i> Download Full Report
    </button>
</div>
        {activeCard === null ? (
          <AssessmentCard
            assessmentCards={assessmentCards}
            outcomes={outcomes}
            handleStartClick={handleStartClick}
            handleViewResponses={handleViewResponses}
            parentAssessmentName={parentAssessmentName || "Assessment"}
            parentAssessmentDesc={parentAssessmentDesc || "No description available"}
          />
        ) : (
          <StartAssessment
            activeCard={activeCard}
            activeCardTitle={activeCardTitle}
            access_token={access_token}
            userId={userId}
            onBack={() => setActiveCard(null)}
            onComplete={(cardId, cardTitle) => {
              const encodedData = encodeData(cardTitle);
              navigate(`/user/results/${cardId}?details=${encodedData}`);
            }}
          />
        )}
      </div>
      

      {assessmentAdded && (
        <Modal
          title="Assessment Submitted!"
          modalType="success"
          bodyText="Records stored successfully.."
          modalId="loginModal"
        />
      )}
    </div>
  );
};

export default User_Assessments;
